import React from "react";
import { Table, Tag, Typography, Tooltip, Switch, Space, Button, Popconfirm } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
} from "@ant-design/icons";

const { Text } = Typography;

const PROVIDER_COLORS = {
  openai: "blue",
  anthropic: "purple",
  gemini: "green",
  groq: "magenta",
  openrouter: "orange",
};

const AiProvidersTable = ({
  providers,
  loading,
  onEdit,
  onDelete,
  onToggle,
  togglingKey,
  onManageModels,
  canDelete,
}) => {
  const columns = [
    {
      title: "Key",
      dataIndex: "provider_key",
      key: "provider_key",
      width: 130,
      render: (val) => (
        <Tag color={PROVIDER_COLORS[val] || "default"} style={{ textTransform: "lowercase" }}>
          {val}
        </Tag>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: "API Key",
      dataIndex: "api_key_masked",
      key: "api_key_masked",
      width: 160,
      render: (val, record) =>
        record.has_api_key ? (
          <Space size={4}>
            <CheckCircleTwoTone twoToneColor="#52c41a" />
            <Text code style={{ fontSize: 12 }}>{val || "••••"}</Text>
          </Space>
        ) : (
          <Space size={4}>
            <CloseCircleTwoTone twoToneColor="#ff4d4f" />
            <Text type="secondary" style={{ fontSize: 12 }}>not set</Text>
          </Space>
        ),
    },
    {
      title: "Models",
      dataIndex: "models",
      key: "models",
      width: 100,
      align: "center",
      render: (models = []) => {
        const enabledCount = models.filter((m) => m.enabled).length;
        return (
          <Tag color={models.length ? "blue" : "default"}>
            {enabledCount}/{models.length}
          </Tag>
        );
      },
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      width: 80,
      align: "center",
      render: (val) => <Text type="secondary">v{val ?? 1}</Text>,
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      width: 80,
      align: "center",
      render: (val, record) => (
        <Switch
          checked={val}
          size="small"
          loading={togglingKey === record.provider_key}
          onChange={() => onToggle(record)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      fixed: "right",
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Manage models">
            <Button
              icon={<AppstoreOutlined />}
              size="small"
              onClick={() => onManageModels(record)}
            />
          </Tooltip>
          <Tooltip title="Edit provider">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          {canDelete && (
            <Popconfirm
              title={`Delete provider "${record.provider_key}"?`}
              description="This permanently removes the provider and all its models."
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
              onConfirm={() => onDelete(record)}
            >
              <Tooltip title="Delete provider (super admin)">
                <Button danger icon={<DeleteOutlined />} size="small" />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={providers}
      loading={loading}
      rowKey={(r) => r._id || r.provider_key}
      pagination={{ pageSize: 10, showSizeChanger: false }}
      scroll={{ x: 1000 }}
      size="middle"
    />
  );
};

export default AiProvidersTable;
